import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  RefreshCw, ChevronDown, ChevronUp, Mail, Phone, MapPin, Calendar, 
  DollarSign, Music, Sparkles, Clock, PhoneCall, Check, X, AlertCircle,
  FileText, Users
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface Package {
  name: string;
  price: number;
  musicians?: string[];
  description?: string;
  eventIndex?: number;
}

interface CulturalAnalysis {
  primaryCulture?: string;
  customerMessage?: string;
  reasoning?: string;
  recommendedInstruments?: string[];
  recommendedMoments?: string[];
  confidence?: number;
}

interface Song {
  song_title?: string;
  artist?: string;
  album?: string;
  spotify_uri?: string;
  event_index?: number;
}

interface InquirySubmission {
  id: string;
  name: string;
  email: string;
  phone: string;
  venue_address: string;
  event_date: string;
  event_start_time: string;
  event_end_time: string;
  load_in_time: string | null;
  notes: string | null;
  created_at: string;
  user_id: string | null;
  event_dates_json: any;
  session_id: string | null;
  total_cost: number | null;
  total_savings: number | null;
  packages_json: Package[] | unknown | null;
  cultural_analysis_json: CulturalAnalysis | CulturalAnalysis[] | unknown | null;
  songs_json: Song[] | unknown | null;
  occasion: string | null;
  vibes: string[] | null;
  user_timezone: string | null;
  status: string;
  scheduled_call_at: string | null;
  scheduled_call_notes: string | null;
}

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'bg-yellow-500/20 text-yellow-700 border-yellow-500/30' },
  { value: 'contacted', label: 'Contacted', color: 'bg-blue-500/20 text-blue-700 border-blue-500/30' },
  { value: 'call_scheduled', label: 'Call Scheduled', color: 'bg-purple-500/20 text-purple-700 border-purple-500/30' },
  { value: 'quoted', label: 'Quoted', color: 'bg-orange-500/20 text-orange-700 border-orange-500/30' },
  { value: 'confirmed', label: 'Confirmed', color: 'bg-green-500/20 text-green-700 border-green-500/30' },
  { value: 'completed', label: 'Completed', color: 'bg-emerald-500/20 text-emerald-700 border-emerald-500/30' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-500/20 text-red-700 border-red-500/30' },
];

export const InquiriesTable = () => {
  const [inquiries, setInquiries] = useState<InquirySubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState<InquirySubmission | null>(null);
  const [callDate, setCallDate] = useState("");
  const [callTime, setCallTime] = useState("");
  const [callNotes, setCallNotes] = useState("");
  const { toast } = useToast();

  const fetchInquiries = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("contact_submissions")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching inquiries:", error);
      toast({
        title: "Error",
        description: "Failed to load inquiries",
        variant: "destructive",
      });
    } else {
      setInquiries((data as InquirySubmission[]) || []);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  const toggleExpand = (id: string) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const formatTime = (time: string) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) return "$0";
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusOption = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return (
      <Badge className={`${statusOption.color} border`}>
        {statusOption.label}
      </Badge>
    );
  };

  const handleStatusChange = async (inquiryId: string, newStatus: string) => {
    const { error } = await supabase
      .from("contact_submissions")
      .update({ status: newStatus })
      .eq("id", inquiryId);

    if (error) {
      console.error("Error updating status:", error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
    } else {
      setInquiries(prev => prev.map(inq => 
        inq.id === inquiryId ? { ...inq, status: newStatus } : inq
      ));
      toast({
        title: "Status Updated",
        description: `Inquiry status changed to ${STATUS_OPTIONS.find(s => s.value === newStatus)?.label}`,
      });
    }
  };

  const openScheduleDialog = (inquiry: InquirySubmission) => {
    setSelectedInquiry(inquiry);
    if (inquiry.scheduled_call_at) {
      const date = new Date(inquiry.scheduled_call_at);
      setCallDate(format(date, "yyyy-MM-dd"));
      setCallTime(format(date, "HH:mm"));
    } else {
      setCallDate("");
      setCallTime("");
    }
    setCallNotes(inquiry.scheduled_call_notes || "");
    setScheduleDialogOpen(true);
  };

  const handleScheduleCall = async () => {
    if (!selectedInquiry || !callDate || !callTime) {
      toast({
        title: "Missing Information",
        description: "Please select a date and time for the call",
        variant: "destructive",
      });
      return;
    }

    const scheduledAt = new Date(`${callDate}T${callTime}`).toISOString();

    const { error } = await supabase
      .from("contact_submissions")
      .update({ 
        scheduled_call_at: scheduledAt,
        scheduled_call_notes: callNotes,
        status: 'call_scheduled'
      })
      .eq("id", selectedInquiry.id);

    if (error) {
      console.error("Error scheduling call:", error);
      toast({
        title: "Error",
        description: "Failed to schedule call",
        variant: "destructive",
      });
    } else {
      setInquiries(prev => prev.map(inq => 
        inq.id === selectedInquiry.id 
          ? { ...inq, scheduled_call_at: scheduledAt, scheduled_call_notes: callNotes, status: 'call_scheduled' } 
          : inq
      ));
      toast({
        title: "Call Scheduled",
        description: `Call scheduled with ${selectedInquiry.name} for ${format(new Date(scheduledAt), "PPP 'at' p")}`,
      });
      setScheduleDialogOpen(false);
    }
  };

  const cancelScheduledCall = async (inquiryId: string) => {
    const { error } = await supabase
      .from("contact_submissions")
      .update({ 
        scheduled_call_at: null,
        scheduled_call_notes: null,
        status: 'contacted'
      })
      .eq("id", inquiryId);

    if (error) {
      console.error("Error cancelling call:", error);
      toast({
        title: "Error",
        description: "Failed to cancel scheduled call",
        variant: "destructive",
      });
    } else {
      setInquiries(prev => prev.map(inq => 
        inq.id === inquiryId 
          ? { ...inq, scheduled_call_at: null, scheduled_call_notes: null, status: 'contacted' } 
          : inq
      ));
      toast({
        title: "Call Cancelled",
        description: "Scheduled call has been cancelled",
      });
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>All Inquiries</CardTitle>
            <CardDescription>
              View and manage all booking inquiries ({inquiries.length} total)
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchInquiries}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : inquiries.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No inquiries yet.
            </p>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Event Date(s)</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Scheduled Call</TableHead>
                    <TableHead>Submitted</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inquiries.map((inquiry) => (
                    <>
                      <TableRow
                        key={inquiry.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleExpand(inquiry.id)}
                      >
                        <TableCell>
                          {expandedRow === inquiry.id ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{inquiry.name}</TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1 text-sm">
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {inquiry.email}
                            </span>
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <Phone className="h-3 w-3" />
                              {inquiry.phone}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {inquiry.event_dates_json && inquiry.event_dates_json.length > 0 ? (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {inquiry.event_dates_json.length} date(s)
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(inquiry.event_date), "MMM d, yyyy")}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="flex items-center gap-1 font-semibold text-primary">
                            <DollarSign className="h-3 w-3" />
                            {formatCurrency(inquiry.total_cost)}
                          </span>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Select
                            value={inquiry.status || 'pending'}
                            onValueChange={(value) => handleStatusChange(inquiry.id, value)}
                          >
                            <SelectTrigger className="w-[130px] h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((status) => (
                                <SelectItem key={status.value} value={status.value}>
                                  {status.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {inquiry.scheduled_call_at ? (
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="bg-purple-500/10 text-purple-700 border-purple-500/30">
                                <PhoneCall className="h-3 w-3 mr-1" />
                                {format(new Date(inquiry.scheduled_call_at), "MMM d, p")}
                              </Badge>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-6 w-6 p-0"
                                onClick={() => openScheduleDialog(inquiry)}
                              >
                                <Clock className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => openScheduleDialog(inquiry)}
                            >
                              <PhoneCall className="h-3 w-3 mr-1" />
                              Schedule
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {format(new Date(inquiry.created_at), "MMM d, yyyy h:mm a")}
                        </TableCell>
                      </TableRow>
                      {expandedRow === inquiry.id && (
                        <TableRow key={`${inquiry.id}-details`}>
                          <TableCell colSpan={8} className="bg-muted/30 p-4">
                            <div className="space-y-6">
                              {/* Quick Actions */}
                              <div className="flex flex-wrap gap-2 pb-4 border-b">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => window.open(`mailto:${inquiry.email}`, '_blank')}
                                >
                                  <Mail className="h-4 w-4 mr-2" />
                                  Email
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => window.open(`tel:${inquiry.phone}`, '_blank')}
                                >
                                  <Phone className="h-4 w-4 mr-2" />
                                  Call
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => openScheduleDialog(inquiry)}
                                >
                                  <PhoneCall className="h-4 w-4 mr-2" />
                                  {inquiry.scheduled_call_at ? 'Reschedule Call' : 'Schedule Call'}
                                </Button>
                                {inquiry.scheduled_call_at && (
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => cancelScheduledCall(inquiry.id)}
                                  >
                                    <X className="h-4 w-4 mr-2" />
                                    Cancel Scheduled Call
                                  </Button>
                                )}
                              </div>

                              {/* Scheduled Call Info */}
                              {inquiry.scheduled_call_at && (
                                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                                  <h4 className="font-semibold mb-2 flex items-center gap-2 text-purple-700">
                                    <PhoneCall className="h-4 w-4" /> Scheduled Call
                                  </h4>
                                  <p className="text-sm">
                                    <span className="text-muted-foreground">Date & Time: </span>
                                    <span className="font-medium">{format(new Date(inquiry.scheduled_call_at), "PPPP 'at' p")}</span>
                                  </p>
                                  {inquiry.scheduled_call_notes && (
                                    <p className="text-sm mt-1">
                                      <span className="text-muted-foreground">Notes: </span>
                                      {inquiry.scheduled_call_notes}
                                    </p>
                                  )}
                                </div>
                              )}

                              {/* Session & Cost Info */}
                              <div className="flex flex-wrap gap-4 text-sm">
                                {inquiry.session_id && (
                                  <div>
                                    <span className="text-muted-foreground">Session ID: </span>
                                    <code className="bg-background px-1 rounded text-xs">{inquiry.session_id}</code>
                                  </div>
                                )}
                                {inquiry.user_timezone && (
                                  <div>
                                    <span className="text-muted-foreground">Timezone: </span>
                                    <span>{inquiry.user_timezone}</span>
                                  </div>
                                )}
                                <div>
                                  <span className="text-muted-foreground">Total Cost: </span>
                                  <span className="font-semibold text-primary">{formatCurrency(inquiry.total_cost)}</span>
                                </div>
                                {inquiry.total_savings && inquiry.total_savings > 0 && (
                                  <div>
                                    <span className="text-muted-foreground">Savings: </span>
                                    <span className="font-semibold text-green-600">{formatCurrency(inquiry.total_savings)}</span>
                                  </div>
                                )}
                              </div>

                              <div className="grid gap-6 md:grid-cols-2">
                                {/* Contact Info */}
                                <div>
                                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                                    <Mail className="h-4 w-4" /> Contact Information
                                  </h4>
                                  <div className="bg-background p-3 rounded border space-y-1 text-sm">
                                    <p><span className="text-muted-foreground">Name:</span> {inquiry.name}</p>
                                    <p><span className="text-muted-foreground">Email:</span> {inquiry.email}</p>
                                    <p><span className="text-muted-foreground">Phone:</span> {inquiry.phone}</p>
                                    <p className="text-muted-foreground text-xs mt-2">
                                      {inquiry.user_id ? `Registered User (${inquiry.user_id.slice(0, 8)}...)` : "Guest User"}
                                    </p>
                                  </div>
                                </div>

                                {/* Venue */}
                                <div>
                                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                                    <MapPin className="h-4 w-4" /> Primary Venue
                                  </h4>
                                  <p className="text-sm bg-background p-3 rounded border">{inquiry.venue_address}</p>
                                </div>

                                {/* Event Details - Matching Email Format */}
                                <div className="md:col-span-2">
                                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                                    <Calendar className="h-4 w-4" /> 📅 Event Details {inquiry.event_dates_json?.length > 1 ? `(${inquiry.event_dates_json.length} Events)` : ''}
                                  </h4>
                                  <div className="bg-background p-3 rounded border space-y-3 text-sm">
                                    {inquiry.event_dates_json && inquiry.event_dates_json.length > 0 ? (
                                      inquiry.event_dates_json.map((evt: any, idx: number) => {
                                        const eventPackage = Array.isArray(inquiry.packages_json) 
                                          ? inquiry.packages_json.find((p: Package) => p.eventIndex === idx)
                                          : null;
                                        
                                        return (
                                          <div key={idx} className="bg-muted/50 p-4 rounded border-l-4 border-primary space-y-3">
                                            <p className="font-semibold text-primary text-lg">
                                              Event {idx + 1}{evt.occasion ? ` - ${evt.occasion}` : ''}
                                            </p>
                                            <div className="grid md:grid-cols-2 gap-3">
                                              <div className="space-y-1">
                                                {evt.date && (
                                                  <p><span className="text-muted-foreground">📅 Date:</span> {format(new Date(evt.date), "MMMM d, yyyy")}</p>
                                                )}
                                                {evt.startTime && evt.endTime && (
                                                  <p><span className="text-muted-foreground">⏰ Time:</span> {formatTime(evt.startTime)} - {formatTime(evt.endTime)}</p>
                                                )}
                                                {evt.loadInTime && (
                                                  <p><span className="text-muted-foreground">🚚 Load-in:</span> {formatTime(evt.loadInTime)}</p>
                                                )}
                                                {(evt.venue || evt.venueAddress) && (
                                                  <p><span className="text-muted-foreground">📍 Venue:</span> {evt.venue || evt.venueAddress}</p>
                                                )}
                                              </div>
                                              <div className="space-y-1">
                                                {evt.guestCount && (
                                                  <p><span className="text-muted-foreground">👥 Guest Count:</span> {evt.guestCount}</p>
                                                )}
                                                {evt.vibes && evt.vibes.length > 0 && (
                                                  <p>
                                                    <span className="text-muted-foreground">✨ Vibes:</span>{" "}
                                                    {evt.vibes.join(', ')}
                                                  </p>
                                                )}
                                                {evt.genres && evt.genres.length > 0 && (
                                                  <p>
                                                    <span className="text-muted-foreground">🎵 Genres:</span>{" "}
                                                    {evt.genres.join(', ')}
                                                  </p>
                                                )}
                                              </div>
                                            </div>
                                            {eventPackage && (
                                              <div className="mt-2 pt-2 border-t border-muted">
                                                <p className="font-medium text-primary flex items-center gap-2">
                                                  <Music className="h-4 w-4" /> {eventPackage.name} - {formatCurrency(eventPackage.price)}
                                                </p>
                                                {eventPackage.musicians && eventPackage.musicians.length > 0 && (
                                                  <p className="text-xs text-muted-foreground mt-1">
                                                    Musicians: {eventPackage.musicians.join(", ")}
                                                  </p>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })
                                    ) : (
                                      <div className="bg-muted/50 p-3 rounded border-l-4 border-primary">
                                        {inquiry.occasion && (
                                          <p className="font-semibold text-primary mb-2">{inquiry.occasion}</p>
                                        )}
                                        <div className="grid gap-1">
                                          <p><span className="text-muted-foreground">📅 Date:</span> {format(new Date(inquiry.event_date), "MMMM d, yyyy")}</p>
                                          <p><span className="text-muted-foreground">⏰ Time:</span> {formatTime(inquiry.event_start_time)} - {formatTime(inquiry.event_end_time)}</p>
                                          {inquiry.load_in_time && (
                                            <p><span className="text-muted-foreground">🚚 Load-in:</span> {formatTime(inquiry.load_in_time)}</p>
                                          )}
                                          <p><span className="text-muted-foreground">📍 Venue:</span> {inquiry.venue_address}</p>
                                          {inquiry.vibes && inquiry.vibes.length > 0 && (
                                            <p>
                                              <span className="text-muted-foreground">✨ Vibes:</span>{" "}
                                              {inquiry.vibes.join(', ')}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Selected Packages */}
                                {inquiry.packages_json && Array.isArray(inquiry.packages_json) && inquiry.packages_json.length > 0 && (
                                  <div>
                                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                                      <Music className="h-4 w-4" /> Selected Packages ({inquiry.packages_json.length})
                                    </h4>
                                    <div className="space-y-2">
                                      {inquiry.packages_json.map((pkg, idx) => (
                                        <div key={idx} className="bg-background p-3 rounded border text-sm">
                                          <div className="flex justify-between items-start">
                                            <span className="font-medium">{pkg.name}</span>
                                            <span className="text-primary font-semibold">{formatCurrency(pkg.price)}</span>
                                          </div>
                                          {pkg.eventIndex !== undefined && (
                                            <p className="text-xs text-muted-foreground">For Event {pkg.eventIndex + 1}</p>
                                          )}
                                          {pkg.musicians && pkg.musicians.length > 0 && (
                                            <p className="text-xs text-muted-foreground mt-1">
                                              Musicians: {pkg.musicians.join(", ")}
                                            </p>
                                          )}
                                          {pkg.description && (
                                            <p className="text-xs text-muted-foreground mt-1">{pkg.description}</p>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Cultural Analysis - Per Event */}
                              {inquiry.cultural_analysis_json && Array.isArray(inquiry.cultural_analysis_json) && inquiry.cultural_analysis_json.length > 0 && (
                                <div>
                                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                                    <Sparkles className="h-4 w-4" /> AI Cultural Analysis ({inquiry.cultural_analysis_json.length} Event{inquiry.cultural_analysis_json.length > 1 ? 's' : ''})
                                  </h4>
                                  <div className="space-y-3">
                                    {inquiry.cultural_analysis_json.map((analysis: CulturalAnalysis, idx: number) => {
                                      const eventDate = inquiry.event_dates_json?.[idx];
                                      return (
                                        <div key={idx} className="bg-background p-3 rounded border text-sm space-y-2">
                                          <div className="flex items-center gap-2 font-medium text-primary border-b pb-2">
                                            <span>Event {idx + 1}</span>
                                            {eventDate && (
                                              <span className="text-muted-foreground text-xs">
                                                - {format(new Date(eventDate.date), "MMM d, yyyy")} ({eventDate.occasion || 'Event'})
                                              </span>
                                            )}
                                          </div>
                                          {analysis.primaryCulture && (
                                            <p>
                                              <span className="text-muted-foreground">Primary Culture:</span>{" "}
                                              <Badge variant="secondary">{analysis.primaryCulture}</Badge>
                                              {analysis.confidence && (
                                                <span className="text-xs text-muted-foreground ml-2">
                                                  ({analysis.confidence}% confidence)
                                                </span>
                                              )}
                                            </p>
                                          )}
                                          {analysis.reasoning && (
                                            <p className="text-muted-foreground text-xs italic">
                                              {analysis.reasoning}
                                            </p>
                                          )}
                                          {analysis.recommendedInstruments && analysis.recommendedInstruments.length > 0 && (
                                            <p>
                                              <span className="text-muted-foreground">AI Recommended Instruments:</span>{" "}
                                              {analysis.recommendedInstruments.join(", ")}
                                            </p>
                                          )}
                                          {analysis.recommendedMoments && analysis.recommendedMoments.length > 0 && (
                                            <p>
                                              <span className="text-muted-foreground">Recommended Moments:</span>{" "}
                                              {analysis.recommendedMoments.join(", ")}
                                            </p>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              {/* Fallback for single analysis object */}
                              {inquiry.cultural_analysis_json && !Array.isArray(inquiry.cultural_analysis_json) && typeof inquiry.cultural_analysis_json === 'object' && Object.keys(inquiry.cultural_analysis_json).length > 0 && (
                                <div>
                                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                                    <Sparkles className="h-4 w-4" /> AI Cultural Analysis
                                  </h4>
                                  {(() => {
                                    const analysis = inquiry.cultural_analysis_json as CulturalAnalysis;
                                    return (
                                      <div className="bg-background p-3 rounded border text-sm space-y-2">
                                        {analysis.primaryCulture && (
                                          <p>
                                            <span className="text-muted-foreground">Primary Culture:</span>{" "}
                                            <Badge variant="secondary">{analysis.primaryCulture}</Badge>
                                            {analysis.confidence && (
                                              <span className="text-xs text-muted-foreground ml-2">
                                                ({analysis.confidence}% confidence)
                                              </span>
                                            )}
                                          </p>
                                        )}
                                        {analysis.reasoning && (
                                          <p className="text-muted-foreground text-xs italic">
                                            {analysis.reasoning}
                                          </p>
                                        )}
                                        {analysis.recommendedInstruments && analysis.recommendedInstruments.length > 0 && (
                                          <p>
                                            <span className="text-muted-foreground">Recommended Instruments:</span>{" "}
                                            {analysis.recommendedInstruments.join(", ")}
                                          </p>
                                        )}
                                        {analysis.recommendedMoments && analysis.recommendedMoments.length > 0 && (
                                          <p>
                                            <span className="text-muted-foreground">Recommended Moments:</span>{" "}
                                            {analysis.recommendedMoments.join(", ")}
                                          </p>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              )}

                              {/* Playlist - Grouped by Event */}
                              {inquiry.songs_json && Array.isArray(inquiry.songs_json) && inquiry.songs_json.length > 0 && (
                                <div>
                                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                                    <Music className="h-4 w-4" /> Playlist ({inquiry.songs_json.length} songs)
                                  </h4>
                                  <div className="bg-background p-3 rounded border">
                                    {(() => {
                                      // Group songs by event_index
                                      const songsByEvent = inquiry.songs_json.reduce((acc: Record<number, Song[]>, song: Song) => {
                                        const eventIdx = song.event_index ?? 0;
                                        if (!acc[eventIdx]) acc[eventIdx] = [];
                                        acc[eventIdx].push(song);
                                        return acc;
                                      }, {});
                                      
                                      const eventIndices = Object.keys(songsByEvent).map(Number).sort((a, b) => a - b);
                                      const hasMultipleEvents = eventIndices.length > 1 || (eventIndices.length === 1 && eventIndices[0] > 0);
                                      
                                      return (
                                        <div className="space-y-3 max-h-64 overflow-y-auto">
                                          {eventIndices.map(eventIdx => {
                                            const eventDate = inquiry.event_dates_json?.[eventIdx];
                                            const songs = songsByEvent[eventIdx];
                                            return (
                                              <div key={eventIdx}>
                                                {hasMultipleEvents && (
                                                  <div className="font-medium text-xs text-primary border-b pb-1 mb-2">
                                                    Event {eventIdx + 1}
                                                    {eventDate && (
                                                      <span className="text-muted-foreground ml-1">
                                                        - {format(new Date(eventDate.date), "MMM d, yyyy")} ({eventDate.occasion || 'Event'})
                                                      </span>
                                                    )}
                                                    <span className="text-muted-foreground ml-1">- {songs.length} songs</span>
                                                  </div>
                                                )}
                                                <div className="grid gap-1 text-sm">
                                                  {songs.map((song: Song, idx: number) => (
                                                    <div key={idx} className="flex items-center gap-2 py-1 border-b border-muted last:border-0">
                                                      <span className="text-muted-foreground w-6">{idx + 1}.</span>
                                                      <div className="flex-1">
                                                        <span className="font-medium">{song.song_title || "Unknown"}</span>
                                                        <span className="text-muted-foreground"> - {song.artist || "Unknown Artist"}</span>
                                                        {song.album && <span className="text-xs text-muted-foreground ml-1">({song.album})</span>}
                                                      </div>
                                                      {song.spotify_uri && (
                                                        <a
                                                          href={`https://open.spotify.com/track/${song.spotify_uri.split(":").pop()}`}
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          className="text-green-500 hover:text-green-400 text-xs"
                                                          onClick={(e) => e.stopPropagation()}
                                                        >
                                                          Spotify ↗
                                                        </a>
                                                      )}
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              )}

                              {/* Notes */}
                              {inquiry.notes && (
                                <div>
                                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                                    <FileText className="h-4 w-4" /> Additional Notes
                                  </h4>
                                  <p className="text-sm whitespace-pre-wrap bg-background p-3 rounded border">
                                    {inquiry.notes}
                                  </p>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Schedule Call Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule Call with {selectedInquiry?.name}</DialogTitle>
            <DialogDescription>
              Set a date and time to follow up with this inquiry.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="call-date">Date</Label>
                <Input
                  id="call-date"
                  type="date"
                  value={callDate}
                  onChange={(e) => setCallDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="call-time">Time</Label>
                <Input
                  id="call-time"
                  type="time"
                  value={callTime}
                  onChange={(e) => setCallTime(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="call-notes">Notes (optional)</Label>
              <Textarea
                id="call-notes"
                placeholder="Topics to discuss, questions to ask..."
                value={callNotes}
                onChange={(e) => setCallNotes(e.target.value)}
              />
            </div>
            {selectedInquiry && (
              <div className="text-sm text-muted-foreground bg-muted p-3 rounded">
                <p><strong>Contact:</strong> {selectedInquiry.phone}</p>
                <p><strong>Email:</strong> {selectedInquiry.email}</p>
                {selectedInquiry.user_timezone && (
                  <p><strong>Client Timezone:</strong> {selectedInquiry.user_timezone}</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleScheduleCall}>
              <PhoneCall className="h-4 w-4 mr-2" />
              Schedule Call
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
